'use client';

import { SWRConfig } from 'swr';
import { swrFetcher } from '../lib/api';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from '../lib/i18n';
import { ApiError } from '../lib/api';
import { logSlowFetch } from '../lib/errorLogger';

const SLOW_EVENT = 'armorik:swr:slow';
const DONE_EVENT = 'armorik:swr:done';

function SlowLoadingBanner() {
  const { t } = useTranslation();
  const [slowKeys, setSlowKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    const onSlow = (e: Event) => {
      const key = (e as CustomEvent<string>).detail;
      setSlowKeys(prev => { const n = new Set(prev); n.add(key); return n; });
    };
    const onDone = (e: Event) => {
      const key = (e as CustomEvent<string>).detail;
      setSlowKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
    };
    window.addEventListener(SLOW_EVENT, onSlow);
    window.addEventListener(DONE_EVENT, onDone);
    return () => {
      window.removeEventListener(SLOW_EVENT, onSlow);
      window.removeEventListener(DONE_EVENT, onDone);
    };
  }, []);

  if (slowKeys.size === 0) return null;
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9998] flex items-center gap-2 px-4 py-2 rounded-rustic border border-ochre bg-cream shadow-lg text-sm text-bark">
      <span className="inline-block w-4 h-4 border-2 border-ochre border-t-transparent rounded-full animate-spin" />
      {t('loadingSlowMsg')}
    </div>
  );
}

export default function SWRProvider({ children }: { children: ReactNode }) {
  return (
    <SWRConfig
      value={{
        fetcher: swrFetcher,
        keepPreviousData: true,
        loadingTimeout: 8000,
        onLoadingSlow: (key) => {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(SLOW_EVENT, { detail: key }));
            logSlowFetch(key);
          }
        },
        onSuccess: (_data, key) => {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(DONE_EVENT, { detail: key }));
          }
        },
        onError: (_err, key) => {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(DONE_EVENT, { detail: key }));
          }
        },
        onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
          const status = error instanceof ApiError ? error.status : undefined;
          // Client errors won't fix themselves (429 is retried — rate limit is temporary).
          if (status && status >= 400 && status < 500 && status !== 429) return;
          if (retryCount >= 10) return;
          setTimeout(() => revalidate({ retryCount }), 3000);
        },
      }}
    >
      {children}
      <SlowLoadingBanner />
    </SWRConfig>
  );
}
